// apps/office/src/pages/office/InteriorQuotationList.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, FileText, Loader2, Eye, Edit, Trash2,
  Printer, Mail, Calendar, Loader, Home, Copy, LayoutGrid, List as ListIcon,
  Send,
  CheckCircle,
  XCircle,
  TrendingUp,
  Lock,
  Settings,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import InteriorQuotationPricingDialog from '../../components/office/InteriorQuotationPricingDialog';

const rs = (v) => {
  const n = Number(v) || 0;
  return `₹ ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n)}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

const STATUS_COLORS = {
  Draft: 'bg-slate-100 text-slate-700 border-slate-300',
  Sent: 'bg-blue-100 text-blue-700 border-blue-300',
  Approved: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  Rejected: 'bg-red-100 text-red-700 border-red-300',
  Invoiced: 'bg-purple-100 text-purple-700 border-purple-300',
};

const STATUS_BAND = {
  Draft: 'bg-slate-400',
  Sent: 'bg-blue-500',
  Approved: 'bg-emerald-500',
  Rejected: 'bg-red-500',
  Invoiced: 'bg-purple-500',
};

const VIEW_KEY = 'interior-quotations-view-style';

const InteriorQuotationList = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [viewStyle, setViewStyle] = useState('list'); // 'list' | 'grid'
  const [pricingQuote, setPricingQuote] = useState(null);
  const [pricingOpen, setPricingOpen] = useState(false);

  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  /* ─── Load saved view preference ─── */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(VIEW_KEY);
      if (saved === 'list' || saved === 'grid') setViewStyle(saved);
    } catch {}
  }, []);

  const changeView = (style) => {
    setViewStyle(style);
    try { localStorage.setItem(VIEW_KEY, style); } catch {}
  };

  /* ─── Project ─── */
  const { data: project, isLoading: projectLoading, error: projectError } = useQuery({
    queryKey: ['interior-project', projectId],
    queryFn: () => pb.collection('interior_projects').getOne(projectId),
    enabled: !!projectId,
    retry: 1,
  });

  /* ─── Quotations ─── */
  const {
    data: quotations = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['interior-quotations', projectId],
    queryFn: () =>
      pb.collection('interior_quotations').getFullList({
        sort: '-created',
        filter: `projectId = "${projectId}" && isLatest = true`,
      }),
    enabled: !!projectId,
    retry: 1,
  });

  /* ─── Delete ─── */
  const deleteMutation = useMutation({
    mutationFn: (id) => pb.collection('interior_quotations').delete(id),
    onSuccess: () => {
      toast.success('Quotation deleted');
      queryClient.invalidateQueries(['interior-quotations', projectId]);
      refetch();
    },
    onError: (err) => toast.error('Delete failed: ' + err.message),
    onSettled: () => setDeletingId(null),
  });

  /* ─── Send for Approval ─── */
  const sendForApprovalMutation = useMutation({
    mutationFn: async (quote) => {
      /* Notify all admins */
      try {
        const admins = await pb.collection('users').getFullList({
          filter: 'role = "admin" || role = "super_admin" || role = "manager"',
        });
        for (const admin of admins) {
          try {
            await pb.collection('notifications').create({
              userId: admin.id,
              type: 'order_placed',
              message: `${user?.name || 'User'} sent interior quotation ${quote.quoteNumber} for approval.`,
              isRead: false,
            });
          } catch {}
        }
      } catch {}

      return pb.collection('interior_quotations').update(quote.id, {
        status: 'Pending Approval',
      });
    },
    onSuccess: (_, quote) => {
      toast.success(`✅ ${quote.quoteNumber} sent for approval`);
      queryClient.invalidateQueries(['interior-quotations', projectId]);
      refetch();
    },
    onError: (err) => toast.error('Send failed: ' + err.message),
  });

  const handleSendForApproval = (quote) => {
    if (!window.confirm(`Send ${quote.quoteNumber} for approval?
All admins will be notified.`)) return;
    sendForApprovalMutation.mutate(quote);
  };

  /* ─── Duplicate ─── */
  const duplicateMutation = useMutation({
    mutationFn: async (quote) => {
      /* Generate next quote number */
      const list = await pb.collection('interior_quotations').getFullList({
        sort: '-created',
        fields: 'quoteNumber',
      });
      let max = 99100;
      list.forEach((q) => {
        const m = (q.quoteNumber || '').match(/IQ(\d+)/);
        if (m) max = Math.max(max, parseInt(m[1], 10));
      });
      const newQuoteNumber = `IQ${max + 1}`;

      /* Deep clone items with new IDs (optional — keep old IDs for safety) */
      const clonedItems = Array.isArray(quote.items)
        ? JSON.parse(JSON.stringify(quote.items))
        : [];

      const payload = {
        quoteNumber: newQuoteNumber,
        projectId: quote.projectId,
        clientId: quote.clientId || '',
        categoryType: quote.categoryType || 'residential',
        mainCategory: quote.mainCategory || 'Standard',
        items: clonedItems,
        subtotal: quote.subtotal || 0,
        cgstEnabled: quote.cgstEnabled || false,
        cgstRate: quote.cgstRate || 9,
        cgstAmount: quote.cgstAmount || 0,
        sgstEnabled: quote.sgstEnabled || false,
        sgstRate: quote.sgstRate || 9,
        sgstAmount: quote.sgstAmount || 0,
        transportation: quote.transportation || 0,
        deepCleaning: quote.deepCleaning || 0,
        discountType: quote.discountType || 'percentage',
        discountValue: quote.discountValue || 0,
        discountAmount: quote.discountAmount || 0,
        total: quote.total || quote.subtotal || 0,
        status: 'Draft',
        notes: quote.notes || '',
        isLatest: true,
        createdBy: user?.id || null,
      };

      return pb.collection('interior_quotations').create(payload);
    },
    onSuccess: (record) => {
      toast.success(`✅ Duplicated as ${record.quoteNumber}`);
      queryClient.invalidateQueries(['interior-quotations', projectId]);
      refetch();
      setDuplicatingId(null);
      /* Navigate to new duplicate */
      setTimeout(() => {
        navigate(`/office/interior-quotations/edit/${record.id}`);
      }, 500);
    },
    onError: (err) => {
      toast.error('Duplicate failed: ' + err.message);
      setDuplicatingId(null);
    },
  });

  const handleDuplicate = (quote) => {
    if (!window.confirm(`Duplicate "${quote.quoteNumber}"? A new quotation will be created.`)) return;
    setDuplicatingId(quote.id);
    duplicateMutation.mutate(quote);
  };

  if (projectLoading || isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-96 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        <p className="text-sm text-slate-500">Loading quotations…</p>
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="p-8 text-center">
        <div className="max-w-lg mx-auto bg-red-50 border-2 border-red-200 rounded-xl p-6">
          <p className="text-red-700 font-bold text-lg mb-2">Cannot load project</p>
          <p className="text-sm text-red-600 mb-4">{projectError?.message || 'Project not found'}</p>
          <Button onClick={() => navigate('/office/interior-projects')} variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Interior Projects
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="max-w-lg mx-auto bg-red-50 border-2 border-red-200 rounded-xl p-6">
          <p className="text-red-700 font-bold text-lg mb-2">Failed to load quotations</p>
          <p className="text-sm text-red-600 mb-4">{error.message}</p>
          <Button onClick={() => refetch()} variant="outline" className="gap-2">
            <Loader className="w-4 h-4" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6 sm:p-8 shadow-xl">
        <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate(-1)}
              className="text-white/80 hover:text-white hover:bg-white/20 -ml-2 mt-1 shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
                <FileText className="w-7 h-7" /> Quotations
              </h1>
              <p className="text-white/80 text-sm mt-1 truncate">
                {project.propertyName}
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/office/interior-quotations/new/${projectId}`)}
            className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-lg gap-2 font-semibold shrink-0"
          >
            <Plus className="w-4 h-4" /> New Quotation
          </Button>
        </div>
      </div>

      {/* View toggle (only if quotations exist) */}
      {quotations.length > 0 && (
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
            <button
              onClick={() => changeView('list')}
              title="List view"
              className={`p-2 rounded-md transition-all ${
                viewStyle === 'list'
                  ? 'bg-white text-emerald-600 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <ListIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => changeView('grid')}
              title="Card view"
              className={`p-2 rounded-md transition-all ${
                viewStyle === 'grid'
                  ? 'bg-white text-emerald-600 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {quotations.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-20 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">No quotations yet</p>
            <p className="text-xs text-slate-400 mt-1">Create the first quotation for this project</p>
            <Button
              onClick={() => navigate(`/office/interior-quotations/new/${projectId}`)}
              className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            >
              <Plus className="w-4 h-4" /> New Quotation
            </Button>
          </CardContent>
        </Card>
      ) : viewStyle === 'list' ? (
        /* ═══════════ LIST VIEW ═══════════ */
        <Card className="border-0 shadow-md overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="text-slate-600 font-semibold">Quote #</TableHead>
                  <TableHead className="text-slate-600 font-semibold">Category</TableHead>
                  <TableHead className="text-slate-600 font-semibold">Style</TableHead>
                  <TableHead className="text-slate-600 font-semibold text-center">Items</TableHead>
                  <TableHead className="text-slate-600 font-semibold">Date</TableHead>
                  <TableHead className="text-slate-600 font-semibold text-right">Total</TableHead>
                  <TableHead className="text-slate-600 font-semibold">Status</TableHead>
                  <TableHead className="text-slate-600 font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotations.map((quote) => {
                  const itemCount = (quote.items || []).reduce(
                    (sum, block) => sum + (block.products?.length || 0), 0
                  );
                  const blockCount = (quote.items || []).length;
                  const isDeleting = deletingId === quote.id;
                  const isDuplicating = duplicatingId === quote.id;

                  return (
                    <TableRow key={quote.id} className="hover:bg-emerald-50/40">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-emerald-700">
                            {quote.quoteNumber}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-slate-100 text-slate-700 border-slate-300 text-xs capitalize">
                          {quote.categoryType || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          (quote.mainCategory || '').toLowerCase() === 'luxury'
                            ? 'bg-amber-100 text-amber-700 border-amber-300 text-xs'
                            : 'bg-blue-100 text-blue-700 border-blue-300 text-xs'
                        }>
                          {quote.mainCategory || 'Standard'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="text-xs text-slate-700">
                          <span className="font-semibold">{blockCount}</span> block{blockCount !== 1 ? 's' : ''}
                          <span className="text-slate-400 mx-1">·</span>
                          <span className="font-semibold">{itemCount}</span> item{itemCount !== 1 ? 's' : ''}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-slate-600 flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-slate-400" />
                          {fmtDate(quote.created)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold text-emerald-700">
                        {rs(quote.total || quote.subtotal || 0)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[quote.status] || STATUS_COLORS.Draft} text-xs border`}>
                          {quote.status || 'Draft'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => navigate(`/office/interior-quotations/view/${quote.id}`)}
                            className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setPricingQuote(quote); setPricingOpen(true); }}
                            className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                            title="Pricing & Charges"
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          {quote.status !== 'Approved' && (
                            <>
  <Button
                              variant="ghost" size="sm"
                              onClick={() => navigate(`/office/interior-quotations/edit/${quote.id}`)}
                              className="h-8 w-8 p-0 text-amber-600 hover:text-amber-800"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handleDuplicate(quote)}
                              disabled={isDuplicating}
                              className="h-8 w-8 p-0 text-indigo-600 hover:text-indigo-800"
                              title="Duplicate"
                            >
                              {isDuplicating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                            </Button>
                            </>
                          )}

                          {/* Send for Approval (Draft only) */}
                          {(quote.status === 'Draft' || !quote.status) && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handleSendForApproval(quote)}
                              disabled={sendForApprovalMutation.isPending}
                              className="h-8 px-2 text-yellow-700 hover:text-yellow-900 bg-yellow-50 hover:bg-yellow-100 gap-1 text-xs font-semibold"
                              title="Send for Approval"
                            >
                              {sendForApprovalMutation.isPending
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <TrendingUp className="w-3.5 h-3.5" />}
                              Send
                            </Button>
                          )}

                          {/* Approved → Locked + Generate Invoice */}
                          {quote.status === 'Approved' && (
                            <>
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 gap-1 text-xs self-center px-2">
                                <Lock className="w-3 h-3" /> Locked
                              </Badge>
                              {isAdmin && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => navigate(`/office/interior-invoices/new/${quote.id}`)}
                                  className="h-8 px-2 text-purple-700 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 gap-1 text-xs font-semibold"
                                  title="Generate Invoice"
                                >
                                  <FileText className="w-3.5 h-3.5" /> Invoice
                                </Button>
                              )}
                            </>
                          )}

                          {isAdmin && quote.status !== 'Approved' && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => {
                                if (window.confirm(`Delete ${quote.quoteNumber}?`)) {
                                  setDeletingId(quote.id);
                                  deleteMutation.mutate(quote.id);
                                }
                              }}
                              disabled={isDeleting}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              title="Delete"
                            >
                              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        /* ═══════════ GRID VIEW ═══════════ */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {quotations.map((quote) => {
            const itemCount = (quote.items || []).reduce(
              (sum, block) => sum + (block.products?.length || 0), 0
            );
            const isDeleting = deletingId === quote.id;
            const isDuplicating = duplicatingId === quote.id;

            return (
              <Card key={quote.id} className="border-0 shadow-md hover:shadow-xl transition-all overflow-hidden">
                <div className={`h-1.5 ${STATUS_BAND[quote.status] || STATUS_BAND.Draft}`} />
                <CardContent className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="font-mono font-bold text-emerald-700 text-lg">{quote.quoteNumber}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                        <Calendar className="w-3 h-3" /> {fmtDate(quote.created)}
                      </p>
                    </div>
                    <Badge className={`${STATUS_COLORS[quote.status] || STATUS_COLORS.Draft} text-xs border`}>
                      {quote.status || 'Draft'}
                    </Badge>
                  </div>

                  {/* Info */}
                  <div className="grid grid-cols-4 gap-3 mb-4 p-3 rounded-lg bg-slate-50">
                    <div>
                      <p className="text-[9px] uppercase text-slate-500 font-semibold">Category</p>
                      <p className="text-xs font-semibold text-slate-700 capitalize mt-0.5">
                        {quote.categoryType || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase text-slate-500 font-semibold">Style</p>
                      <p className="text-xs font-semibold text-slate-700 mt-0.5">
                        {quote.mainCategory || 'Standard'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase text-slate-500 font-semibold">Items</p>
                      <p className="text-xs font-semibold text-slate-700 mt-0.5">{itemCount}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] uppercase text-slate-500 font-semibold">Total</p>
                      <p className="text-sm font-bold text-emerald-700 mt-0.5">{rs(quote.total || quote.subtotal || 0)}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1.5 flex-wrap">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/office/interior-quotations/view/${quote.id}`)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 flex-1"
                    >
                      <Eye className="w-3.5 h-3.5" /> View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setPricingQuote(quote); setPricingOpen(true); }}
                      className="gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      title="Pricing & Charges"
                    >
                      <Settings className="w-3.5 h-3.5" />
                    </Button>
                    {quote.status !== 'Approved' && (
                      <>
    <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/office/interior-quotations/edit/${quote.id}`)}
                          className="gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50"
                          title="Edit"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDuplicate(quote)}
                          disabled={isDuplicating}
                          className="gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                          title="Duplicate"
                        >
                          {isDuplicating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                        </Button>
                      </>
                    )}

                    {(quote.status === 'Draft' || !quote.status) && (
                      <Button
                        size="sm"
                        onClick={() => handleSendForApproval(quote)}
                        disabled={sendForApprovalMutation.isPending}
                        className="gap-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-xs"
                        title="Send for Approval"
                      >
                        {sendForApprovalMutation.isPending
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <TrendingUp className="w-3.5 h-3.5" />}
                        Send
                      </Button>
                    )}

                    {quote.status === 'Approved' && (
                      <>
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 gap-1 text-xs self-center px-2 py-1">
                          <Lock className="w-3 h-3" /> Locked
                        </Badge>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/office/interior-invoices/new/${quote.id}`)}
                            className="gap-1.5 border-purple-200 text-purple-700 hover:bg-purple-50 text-xs"
                            title="Generate Invoice"
                          >
                            <FileText className="w-3.5 h-3.5" /> Invoice
                          </Button>
                        )}
                      </>
                    )}

                    {isAdmin && quote.status !== 'Approved' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (window.confirm(`Delete ${quote.quoteNumber}?`)) {
                            setDeletingId(quote.id);
                            deleteMutation.mutate(quote.id);
                          }
                        }}
                        disabled={isDeleting}
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <InteriorQuotationPricingDialog
        open={pricingOpen}
        onOpenChange={setPricingOpen}
        quotation={pricingQuote}
        onSaved={() => { refetch(); setPricingQuote(null); }}
      />
    </div>
  );
};

export default InteriorQuotationList;
